import { issueHierarchyPageResource } from '@jira/admin-pages__issue-types-issue-hierarchy-directory/src/issueHierarchyPageResource.tsx';
import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { CLASSIC_BACKLOG } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { horizontalNavJiraResource } from '@jira/navigation-apps__resources/src/controllers/horizontal-navigation/horizontal-nav-jira/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';

import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { classicSoftwareBoardSettingsInsightsRapidboardBacklogRoute } from '@jira/router-routes__software-classic-board-settings-insights-routes/src/classicSoftwareBoardSettingsInsightsRapidboardBacklogRoute.tsx';
import {
	LazyRapidBoard,
	LazyRapidBoardApps,
	LazyRapidBoardResource,
	LazyRapidBoardConfigApiResource,
	LazyRapidBoardDataApiResource,
	LazyRapidBoardWrmBundleResource,
} from '@jira/router-routes__user-board-entries/src/ui/async.tsx';
import { NextGenBacklogSkeleton } from '@jira/platform__skeletons/src/ui/next-gen-backlog/NextGenBacklogSkeleton.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { InsightsSettingsRedirect } from './common/index.tsx';
import type { Route } from '@atlassian/react-resource-router';

export const classicSoftwareBoardSettingsInsightsRapidboardBacklogRouteEntry: Route = createEntry(
	classicSoftwareBoardSettingsInsightsRapidboardBacklogRoute,

	() => ({
		group: 'classic-software',
		ufoName: 'rapidboard-backlog-insights',
		// eslint-disable-next-line no-restricted-syntax
		component: InsightsSettingsRedirect,
		skeleton: NextGenBacklogSkeleton,

		layout: composeLayouts(
			genericProjectLayout,
			embeddedIssuePreloadLayoutBuilder([{ query: ['selectedIssue', 'view=planning'] }]),
		),

		navigation: {
			menuId: MenuIdProjects,
			horizontal: AsyncHorizontalNavJira,
		},

		resources: [
			...getNavigationResources(),
			...getIssueViewResources(),
			// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
			// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
			incidentExperimentTraitResource,
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			forgeProjectModuleResource,
			staticProjectSidebarNavigationResource,
			staticProjectSidebarConnectAddonsNavigationResource,
			horizontalNavJiraResource,
			// end of resources expanded from getNavigationSidebarProjectResource
			...getRapidboardResources(),
			getRapidBoardDataResource(),
			issueHierarchyPageResource,
			getBoardDetailsResource(),
		],

		afterPaint: [LazyIssueApp],

		forPaint: [
			LazyAtlassianNavigation,
			LazyRapidBoard,
			LazyRapidBoardApps,
			LazyRapidBoardDataApiResource,
			LazyRapidBoardConfigApiResource,
			LazyRapidBoardWrmBundleResource,
			LazyRapidBoardResource,
		],

		meta: {
			capability: CLASSIC_BACKLOG,
		},

		earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,

		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
